import { useState } from "react";
import { toast } from "sonner";
import { dayLeaderboard, championPointsForDay, initials } from "@/lib/scoring";
import { verifyDay, daySubmissions } from "@/lib/verify";
import { Lock, Unlock, FileText, ClipboardCheck, AlertTriangle } from "lucide-react";
import PlayerCardModal from "@/screens/PlayerCardModal";
import ResultsSheetModal from "@/screens/ResultsSheetModal";
import ScoreReviewModal from "@/screens/ScoreReviewModal";
import { GroupsInPanel } from "@/screens/GroupsInPanel";

export default function LiveTab({ event, currentDay, updateEventLocal, updateScores, canEdit = true }) {
  const [openPlayerId, setOpenPlayerId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  if (!currentDay) return <div className="tga-card-dark text-center py-8 helptext">No day selected.</div>;
  const course = event.courses[currentDay.courseKey];
  if (!course) return <div className="tga-card-dark text-center py-8 helptext">Course missing.</div>;
  const rows = dayLeaderboard(currentDay, event);
  const report = verifyDay(currentDay);
  const subs = daySubmissions(currentDay);

  const doFinalize = () => {
    const results = championPointsForDay(currentDay, event);
    updateEventLocal((ev) => {
      ev.days = ev.days.map((d) => {
        if (d.id !== currentDay.id) return d;
        const handicaps = { ...(d.handicaps || {}) };
        for (const g of d.groups || []) for (const pid of g.playerIds || []) if (typeof handicaps[pid] !== "number") handicaps[pid] = ev.players.find((p) => p.id === pid)?.handicap || 0;
        return { ...d, finalized: true, results, handicaps };
      });
      return ev;
    });
    setReviewOpen(false);
    toast.success(`${currentDay.label} finalized — Champion points awarded`);
    setSheetOpen(true);
  };

  const finalize = () => {
    if (currentDay.finalized) {
      if (!window.confirm("Unlock this day and remove Champion points?")) return;
      updateEventLocal((ev) => {
        ev.days = ev.days.map((d) => d.id === currentDay.id ? { ...d, finalized: false, results: null } : d);
        return ev;
      });
      toast.info(`${currentDay.label} unlocked`);
      return;
    }
    if (!report.ok) {
      toast.error(
        report.discrepancyCount > 0
          ? `Scorecards don't match on ${report.discrepancyCount} hole${report.discrepancyCount === 1 ? "" : "s"} — review before finalising`
          : `${report.missingCount} scorecard${report.missingCount === 1 ? " is" : "s are"} incomplete — every player must enter the group's scores`
      );
      setReviewOpen(true);
      return;
    }
    doFinalize();
  };

  return (
    <div className="space-y-4 tga-fade">
      <div className="tga-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-2xl">{currentDay.label} · Live</div>
            <div className="text-xs text-[color:var(--brass-dark)]">{course.name} · Stableford</div>
          </div>
          <div className="flex items-center gap-2">
            {!currentDay.finalized && (
              <button
                data-testid="live-review-cards-btn"
                className={`rounded-lg px-3 py-2 text-xs inline-flex items-center gap-1 ${report.ok ? "ghost-btn" : "ghost-btn !border-amber-600/60 !text-amber-800"}`}
                onClick={() => setReviewOpen(true)}
                title="Compare every player's scorecard"
              >
                {report.ok ? <ClipboardCheck size={14} /> : <AlertTriangle size={14} />} Cards
                {!report.ok && <span className="font-mono-metrics">{report.missingCount + report.discrepancyCount}</span>}
              </button>
            )}
            {currentDay.finalized && (
              <button
                data-testid="live-results-sheet-btn"
                className="brass-btn rounded-lg px-3 py-2 text-xs inline-flex items-center gap-1"
                onClick={() => setSheetOpen(true)}
              ><FileText size={14}/> Results sheet</button>
            )}
            {canEdit ? (
              <button
                data-testid="live-finalize-btn"
                className={currentDay.finalized ? "ghost-btn rounded-lg px-3 py-2 text-xs inline-flex items-center gap-1" : subs.allIn ? "brass-btn tga-glow rounded-lg px-3 py-2 text-xs inline-flex items-center gap-1" : "ghost-btn rounded-lg px-3 py-2 text-xs inline-flex items-center gap-1"}
                title={!currentDay.finalized && !subs.allIn ? `${subs.submitted}/${subs.total} groups have submitted` : undefined}
                onClick={finalize}
              >
                {currentDay.finalized ? <><Unlock size={14}/> Unlock</> : <><Lock size={14}/> Finalize</>}
              </button>
            ) : currentDay.finalized ? (
              <span className="text-xs text-[color:var(--brass-dark)] inline-flex items-center gap-1" data-testid="live-finalized-badge"><Lock size={12} /> Final</span>
            ) : null}
          </div>
        </div>

        {rows.length === 0 ? (
          <p className="helptext">No scores yet.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm font-mono-metrics">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[rgba(27,51,34,0.15)]">
                  <th className="py-1 px-2">#</th>
                  <th className="py-1 px-2">Player</th>
                  <th className="py-1 px-2 text-right">H</th>
                  <th className="py-1 px-2 text-right">Str</th>
                  <th className="py-1 px-2 text-right" title="Fairways Hit">FH</th>
                  <th className="py-1 px-2 text-right" title="Greens in Regulation">GIR</th>
                  <th className="py-1 px-2 text-right" title="Total putts · avg per hole">Putts</th>
                  <th className="py-1 px-2 text-right">Pts</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.player.id} className="tga-row border-b border-[rgba(27,51,34,0.08)] cursor-pointer" onClick={() => setOpenPlayerId(r.player.id)} data-testid={`live-row-${r.player.id}`}>
                    <td className="py-2 px-2">{i + 1}</td>
                    <td className="py-2 px-2 flex items-center gap-2">
                      <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(r.player.name)}</div>
                      <span className="font-sans">{r.player.name}</span>
                    </td>
                    <td className="py-2 px-2 text-right">{r.holesPlayed}</td>
                    <td className="py-2 px-2 text-right">{r.strokes || "—"}</td>
                    <td className="py-2 px-2 text-right">{r.fhAttempts ? `${r.fhHit}/${r.fhAttempts}` : "—"}</td>
                    <td className="py-2 px-2 text-right">{r.holesPlayed ? `${r.girHit}/${r.holesPlayed}` : "—"}</td>
                    <td className="py-2 px-2 text-right">
                      {r.puttHoles ? <>{r.putts}<span className="text-[10px] opacity-60 ml-1">{(r.putts / r.puttHoles).toFixed(1)}</span></> : "—"}
                    </td>
                    <td className="py-2 px-2 text-right font-bold">{r.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!currentDay.finalized && <GroupsInPanel day={currentDay} updateScores={updateScores} canEdit={canEdit} />}

      {currentDay.finalized && currentDay.results && (
        <div className="tga-card-dark">
          <div className="font-display text-lg text-[color:var(--brass-bright)] mb-2">Champion points awarded</div>
          <div className="space-y-1 text-sm">
            {currentDay.results.map((r) => {
              const p = event.players.find((x) => x.id === r.playerId);
              if (!p) return null;
              return (
                <div key={r.playerId} className="flex justify-between font-mono-metrics">
                  <span className="font-sans">{p.name}</span>
                  <span className="text-[color:var(--brass-bright)]">+{r.championPoints}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <PlayerCardModal
        open={!!openPlayerId}
        onClose={() => setOpenPlayerId(null)}
        event={event}
        playerId={openPlayerId}
        dayId={currentDay?.id}
      />

      <ScoreReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        event={event}
        day={currentDay}
        updateScores={updateScores}
        onFinalizeAnyway={doFinalize}
        canFinalize={canEdit}
      />

      <ResultsSheetModal
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        event={event}
        mode="day"
        day={currentDay}
      />
    </div>
  );
}
